import { Chart } from "primereact/chart";
import { useContext, useEffect, useRef, useState } from "react";
import moment from "moment";
import "chartjs-adapter-moment";

// import "../../PPA/Valuation/Style/PPAStyles.css";
import axios from "../../../interceptors/axiosInstance";
import { LoaderContext } from "../../../context/LoaderContext";
import { config } from "../../../config";
import { numberFormat, twoDecimal, upperCase } from "../../../utils/display";
import { DatePicker } from "../../../components/General/DataEntry/DatePicker/DatePicker";
import "./Report.css";

const GRANULARITY_MAPPER = {
  Day: "day",
  Month: "month",
  Quarter: "quarter",
  Year: "year",
};

const GRANULARITY_DATE_FORMAT = {
  day: "YYYY-MM-DD",
  month: "YYYY-MM",
  quarter: "YYYY-Q",
  year: "YYYY",
};

// const negativePriceData = [
//   {
//     start: "2025-02-01T04:05:00.000Z",
//     end: "2025-02-01T05:00:00.000Z",
//     count: 12,
//   },
//   {
//     start: "2025-02-01T18:05:00.000Z",
//     end: "2025-02-01T19:00:00.000Z",
//     count: 12,
//   },
//   {
//     start: "2025-02-06T08:05:00.000Z",
//     end: "2025-02-06T09:00:00.000Z",
//     count: 15,
//   },
//   {
//     start: "2025-02-19T13:05:00.000Z",
//     end: "2025-02-19T14:00:00.000Z",
//     count: 18,
//   },
//   {
//     start: "2025-03-19T13:05:00.000Z",
//     end: "2025-03-19T14:00:00.000Z",
//     count: 18,
//   },
//   {
//     start: "2025-03-25T13:05:00.000Z",
//     end: "2025-03-25T14:00:00.000Z",
//     count: 18,
//   },
//   {
//     start: "2025-05-10T13:05:00.000Z",
//     end: "2025-05-11T14:00:00.000Z",
//     count: 18,
//   },
// ];

export default function Report({
  contractId,
  view = "portfolio",
  role = "offtaker",
}) {
  const [spotPriceChartData, setSpotPriceChartData] = useState({});
  const [lgcPriceChartData, setLgcPriceChartData] = useState({});
  const [volumeChartData, setVolumeChartData] = useState({});
  const [cumulativeVolumeChartData, setCumulativeVolumeChartData] = useState(
    {},
  );
  const [suppliedVolumeChartData, setSuppliedVolumeChartData] = useState({});
  const [netPositionChartData, setNetPositionChartData] = useState({});
  const [negativePriceChartData, setNegativePriceChartData] = useState({});
  const [shortFallSurplusChartData, setShortFallSurplusChartData] = useState(
    {},
  );
  const [
    cumulativeFixedFloatingChartData,
    setCumulativeFixedFloatingChartData,
  ] = useState({});
  const [mlfPriceChartData, setMlfPriceChartData] = useState({});
  const [
    forecastedAndActualRevenueChartData,
    setForecastedAndActualRevenueChartData,
  ] = useState({});

  const [initialDateRange, setInitialDateRange] = useState({
    startDate: moment().toDate(),
    endDate: moment().toDate(),
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState("");
  const [selectedAsset, setSelectedAsset] = useState("");
  const [currency, setCurrency] = useState("AUD");

  const [originalData, setOriginalData] = useState({});
  const [spotPrices, setSpotPrices] = useState([]);
  const [lgcPrices, setLgcPrices] = useState([]);
  const [contractPerformanceData, setContractPerformanceData] = useState([]);
  const [mlfPrices, setMlfPrices] = useState([]);
  const [granularityFilter, setGranularityFilter] = useState(
    GRANULARITY_MAPPER.Month,
  );
  const [kpis, setKpis] = useState({
    cumulativeFloatingAmount: 0,
    cumulativeFixedAmount: 0,
    RE100Baseline: 0,
    lgcReceived: 0,
    lgcExpected: 0,
    negativePriceCompensation: 0,
    cumulativeForecastedRevenue: 0,
    cumulativeActualRevenue: 0,
  });

  const spotPriceChartRef = useRef(null);
  const lgcPriceChartRef = useRef(null);
  const volumeChartRef = useRef(null);
  const cumulativeVolumeChartRef = useRef(null);
  const suppliedVolumeChartRef = useRef(null);
  const netPositionChartRef = useRef(null);
  const negativePriceChartRef = useRef(null);
  const shortFallSurplusChartRef = useRef(null);
  const cumulativeFixedFloatingChartRef = useRef(null);
  const mlfPriceChartRef = useRef(null);
  const forecastedActualRevenueChartRef = useRef(null);

  const { setLoading, loading } = useContext(LoaderContext);

  const textColor = "rgba(196, 196, 205, 1)";
  const textColorSecondary = "rgba(196, 196, 205, 1)";
  const documentStyle = getComputedStyle(document.documentElement);

  useEffect(() => {
    getChartData(contractId, null, null, true);
  }, [contractId]);

  useEffect(() => {
    configureGraphData(
      spotPrices?.length > 0 ? spotPrices : [],
      "regionId",
      "settlementDate",
      "spotPrice",
      setSpotPriceChartData,
      GRANULARITY_DATE_FORMAT[granularityFilter],
    );
  }, [spotPrices]);

  useEffect(() => {
    configureGraphData(
      lgcPrices?.length > 0 ? lgcPrices : [],
      "location",
      "isoDate",
      "LGC",
      setLgcPriceChartData,
      GRANULARITY_DATE_FORMAT[granularityFilter],
    );
  }, [lgcPrices]);

  useEffect(() => {
    configureGraphData(
      mlfPrices?.length > 0 ? mlfPrices : [],
      "generatorId",
      "startDate",
      "transmissionLossFactor",
      setMlfPriceChartData,
      "YYYY/MM/DD",
    );
  }, [mlfPrices]);

  useEffect(() => {
    processPerformanceData();
  }, [contractPerformanceData]);

  useEffect(() => {
    prepareChartData(
      originalData,
      granularityFilter,
      location,
      selectedAsset,
      true,
    );
  }, [location, selectedAsset]);

  useEffect(() => {
    prepareChartData(
      originalData,
      granularityFilter,
      location,
      selectedAsset,
      false,
    );
  }, [granularityFilter]);

  const processPerformanceData = () => {
    const expectedVolume = {};
    const suppliedVolume = {};
    const cumulativeExpectedVolume = {};
    const cumulativeSuppliedVolume = {};
    const fixedAmount = {};
    const floatingAmount = {};

    const forecastedRevenue = {};
    const actualRevenue = {};
    let cumulativeForecastedRevenue = 0;
    let cumulativeActualRevenue = 0;

    let totalSuppliedVolume = 0;
    let totalExpectedVolume = 0;

    let cumulativeFloatingAmount = 0;
    let cumulativeFixedAmount = 0;
    let RE100Baseline = 0;
    let negativePriceCompensation = 0;
    const shortFallData = [];
    const surplusData = [];

    const cumulativeFixedChartData = {};
    const cumulativeFloatingChartData = {};
    const cumulativeNetPositionChartData = {};
    const cumulativeRE100ChartData = {};

    const negativePriceCompensationData = [];
    const negativePriceEvents = {};

    const lgcRecord = {};

    lgcPrices?.forEach((loc) => {
      loc?.data?.forEach((locationLgc) => {
        const date = moment(
          locationLgc?.isoDate,
          GRANULARITY_DATE_FORMAT[granularityFilter],
        ).format(GRANULARITY_DATE_FORMAT[granularityFilter]);
        if (!lgcRecord[date]) {
          lgcRecord[date] = {
            total: 0,
            count: 0,
          };
        }
        lgcRecord[date].total += locationLgc?.LGC;
        lgcRecord[date].count += 1;
      });
    });

    const lgcPriceForPerformancePeriod = {};

    Object.keys(lgcRecord).forEach((date) => {
      lgcPriceForPerformancePeriod[date] =
        lgcRecord[date]?.total / lgcRecord[date]?.count || 0;
    });

    contractPerformanceData?.forEach((contractPerformance) => {
      contractPerformance?.data?.forEach((performanceForDate) => {
        const performancePeriod = moment(
          performanceForDate?.date,
          GRANULARITY_DATE_FORMAT[granularityFilter],
        ).format(GRANULARITY_DATE_FORMAT[granularityFilter]);

        if (!negativePriceEvents[performancePeriod]) {
          negativePriceEvents[performancePeriod] = [];
        }
        negativePriceEvents[performancePeriod].push(
          ...(performanceForDate?.negativePriceEvents || []),
        );

        expectedVolume[performancePeriod] = expectedVolume[performancePeriod]
          ? expectedVolume[performancePeriod] +
            performanceForDate?.expectedVolume
          : performanceForDate?.expectedVolume;
        suppliedVolume[performancePeriod] = suppliedVolume[performancePeriod]
          ? suppliedVolume[performancePeriod] +
            performanceForDate?.suppliedVolume
          : performanceForDate?.suppliedVolume;

        floatingAmount[performancePeriod] = floatingAmount[performancePeriod]
          ? floatingAmount[performancePeriod] +
            performanceForDate?.floatingAmount
          : performanceForDate?.floatingAmount;
        fixedAmount[performancePeriod] = fixedAmount[performancePeriod]
          ? fixedAmount[performancePeriod] + performanceForDate?.fixedAmount
          : performanceForDate?.fixedAmount;

        forecastedRevenue[performancePeriod] = forecastedRevenue?.[
          performancePeriod
        ]
          ? forecastedRevenue[performancePeriod] +
            (performanceForDate?.forecastedRevenue || 0)
          : performanceForDate?.forecastedRevenue || 0;
        actualRevenue[performancePeriod] = actualRevenue?.[performancePeriod]
          ? actualRevenue[performancePeriod] +
            (performanceForDate?.actualRevenue || 0)
          : performanceForDate?.actualRevenue || 0;

        cumulativeActualRevenue += performanceForDate?.actualRevenue || 0;
        cumulativeForecastedRevenue +=
          performanceForDate?.forecastedRevenue || 0;

        negativePriceCompensation +=
          performanceForDate?.negativePriceCompensation || 0;

        if (negativePriceEvents?.[performancePeriod]?.length > 0) {
          negativePriceCompensationData.push({
            x: performancePeriod,
            y: performanceForDate?.negativePriceCompensation,
          });
        }

        shortFallData.push({
          x: performancePeriod,
          y: performanceForDate?.shortFall,
        });

        surplusData.push({
          x: performancePeriod,
          y: performanceForDate?.surplus,
        });
      });
    });

    Object.keys(expectedVolume).forEach((performancePeriod) => {
      totalExpectedVolume += expectedVolume[performancePeriod];
      totalSuppliedVolume += suppliedVolume[performancePeriod];

      cumulativeExpectedVolume[performancePeriod] = totalExpectedVolume;
      cumulativeSuppliedVolume[performancePeriod] = totalSuppliedVolume;

      cumulativeFixedAmount += fixedAmount[performancePeriod];
      cumulativeFloatingAmount += floatingAmount[performancePeriod];

      cumulativeFixedChartData[performancePeriod] = cumulativeFixedAmount;
      cumulativeFloatingChartData[performancePeriod] = cumulativeFloatingAmount;

      RE100Baseline +=
        (lgcPriceForPerformancePeriod?.[performancePeriod] || 0) *
        suppliedVolume[performancePeriod];

      cumulativeNetPositionChartData[performancePeriod] =
        cumulativeFloatingAmount - cumulativeFixedAmount;
      cumulativeRE100ChartData[performancePeriod] = RE100Baseline;
    });

    setKpis({
      cumulativeFloatingAmount,
      cumulativeFixedAmount,
      RE100Baseline,
      lgcReceived: totalSuppliedVolume,
      lgcExpected: totalExpectedVolume,
      negativePriceCompensation,
      cumulativeActualRevenue,
      cumulativeForecastedRevenue,
    });

    // Expected Vs Actual LGC Chart data
    const chartData = {
      datasets: [
        {
          label: "LGCs/RECs Expected",
          data: Object.keys(expectedVolume).reduce((acc, period) => {
            acc[period] = Math.trunc(expectedVolume[period]);
            return acc;
          }, {}),
          grouped: true,
          backgroundColor: "rgba(255,230,0,1)",
        },
        {
          label: "LGCs/RECs Received",
          data: Object.keys(suppliedVolume).reduce((acc, period) => {
            acc[period] = Math.trunc(suppliedVolume[period]);
            return acc;
          }, {}),
          grouped: true,
          backgroundColor: "rgba(61,185,61,1)",
        },
      ],
    };

    setVolumeChartData(chartData);

    // Cumulative Fixed, Floating, Net Position, RE100 Baseline chart data
    const cumulativeFixedFloatingData = {
      datasets: [
        {
          label: "Fixed Amount",
          data: cumulativeFixedChartData,
          fill: true,
        },
        {
          label: "Floating Amount",
          data: cumulativeFloatingChartData,
          fill: true,
        },
        {
          label: "PPA Net Position",
          data: cumulativeNetPositionChartData,
          fill: true,
        },
        {
          label: "RE 100 Baseline",
          data: cumulativeRE100ChartData,
          fill: true,
        },
      ],
    };
    setCumulativeFixedFloatingChartData(cumulativeFixedFloatingData);

    // Cumulative Expected Vs Cumulative Supplied LGC Chart data
    const cumulativeVolChartData = {
      datasets: [
        {
          label: "Cumulative LGCs/RECs Expected",
          data: Object.keys(cumulativeExpectedVolume).reduce((acc, period) => {
            acc[period] = Math.trunc(cumulativeExpectedVolume[period]);
            return acc;
          }, {}),
          borderColor: "rgba(255,230,0,1)",
          backgroundColor: "rgba(255,230,0,1)",
          grouped: true,
          fill: true,
        },
        {
          label: "Cumulative LGCs/RECs Received",
          data: Object.keys(cumulativeSuppliedVolume).reduce((acc, period) => {
            acc[period] = Math.trunc(cumulativeSuppliedVolume[period]);
            return acc;
          }, {}),
          grouped: true,
          borderColor: "rgba(61,185,61,1)",
          backgroundColor: "rgba(61,185,61,1)",
          fill: true,
        },
      ],
    };
    setCumulativeVolumeChartData(cumulativeVolChartData);

    const negativePriceOccurences = [];

    Object.keys(negativePriceEvents).forEach((dateFormat) => {
      negativePriceEvents[dateFormat]?.forEach((event) => {
        const negativeOccurenceDate = moment(
          event?.startTime,
          "YYYY-MM-DD HH:mm:ss",
        ).utc();

        negativePriceOccurences.push({
          date: dateFormat,
          eventTime: negativeOccurenceDate.toDate(),
        });
      });
    });

    const negativeData = {
      datasets: [
        {
          label: "Negative Price Event",
          data: negativePriceOccurences?.map((negativePrice) => {
            return {
              x: negativePrice?.date,
              y: negativePrice?.eventTime,
              r: 4,
            };
          }),
          type: "bubble",
          backgroundColor: "#ffe600",
        },
        {
          label: "Negative Price Compensation",
          data: negativePriceCompensationData, // Array of { x: date, y: amount }
          type: "line",

          borderColor: "rgba(61,185,61,1)",
          backgroundColor: "rgba(61,185,61,1)",
          // fill: false,
          yAxisID: "y1", // Use the second y-axis for the line chart
        },
      ],
    };

    setNegativePriceChartData(negativeData);

    // ShortFall and Surplus Chart Data
    const shortFallSurplusData = {
      datasets: [
        {
          label: "Shortfall",
          data: shortFallData,
          backgroundColor: "rgb(232,77,67)",
          borderColor: "rgb(232,77,67)",
          fill: true,
        },
        {
          label: "Surplus",
          data: surplusData,
          borderColor: "rgba(61,185,61,1)",
          backgroundColor: "rgba(61,185,61,1)",
          fill: true,
        },
      ],
    };
    setShortFallSurplusChartData(shortFallSurplusData);

    const spotPriceRecord = {};

    spotPrices?.forEach((region) => {
      region?.data?.forEach((regionSpotPrice) => {
        const date = moment(
          regionSpotPrice?.settlementDate,
          GRANULARITY_DATE_FORMAT[granularityFilter],
        ).format(GRANULARITY_DATE_FORMAT[granularityFilter]);
        if (!spotPriceRecord[date]) {
          spotPriceRecord[date] = {
            total: 0,
            count: 0,
          };
        }
        spotPriceRecord[date].total += regionSpotPrice?.spotPrice;
        spotPriceRecord[date].count += 1;
      });
    });

    const spotPriceForPerformancePeriod = {};

    Object.keys(spotPriceRecord).forEach((date) => {
      spotPriceForPerformancePeriod[date] =
        spotPriceRecord[date]?.total / spotPriceRecord[date]?.count || 0;
    });

    const suppliedVolChartData = {
      datasets: [
        {
          type: "line",
          label: "Average Spot Price",
          data: Object.keys(spotPriceForPerformancePeriod)?.map((date) => {
            return {
              x: date,
              y: spotPriceForPerformancePeriod[date],
            };
          }),
          borderColor: "rgba(255,230,0,1)",
          backgroundColor: "rgba(255,230,0,1)",
        },
        {
          type: "bar",
          label: "Supplied Volume (MWh)",
          data: suppliedVolume,
          backgroundColor: "rgba(61,185,61,1)",
        },
      ],
    };

    setSuppliedVolumeChartData(suppliedVolChartData);

    // Forecasted&Actual Revenue Chart Data

    const forecastedAndActualRevenueData = {
      datasets: [
        {
          label: role === "offtaker" ? "Forecasted Cost" : "Forecasted Revenue",
          data: Object.keys(forecastedRevenue).map((performancePeriod) => {
            return {
              x: performancePeriod,
              y: forecastedRevenue[performancePeriod],
            };
          }),
          backgroundColor: "rgb(232,77,67)",
          borderColor: "rgb(232,77,67)",
          fill: true,
        },
        {
          label: role === "offtaker" ? "Actual Cost" : "Actual Revenue",
          data: Object.keys(actualRevenue).map((performancePeriod) => {
            return {
              x: performancePeriod,
              y: actualRevenue[performancePeriod],
            };
          }),
          borderColor: "rgba(61,185,61,1)",
          backgroundColor: "rgba(61,185,61,1)",
          fill: true,
        },
      ],
    };

    setForecastedAndActualRevenueChartData(forecastedAndActualRevenueData);

    // Net Position Chart Data
    const netPositionData = {};

    originalData?.contracts?.forEach((contract) => {
      const asset = contract?.assetId;
      if (!netPositionData[asset]) {
        netPositionData[asset] = { fixedAmount: 0, floatingAmount: 0 };
      }
      const contractPerformances = contractPerformanceData.find(
        (performance) => performance.contractId === contract.id,
      );

      contractPerformances?.data?.forEach((performance) => {
        netPositionData[asset]["fixedAmount"] =
          netPositionData[asset]["fixedAmount"] + performance?.fixedAmount;
        netPositionData[asset]["floatingAmount"] =
          netPositionData[asset]["floatingAmount"] +
          performance?.floatingAmount;
      });
    });

    const netPosDataArr = [];

    Object.keys(netPositionData)?.forEach((asset) => {
      netPosDataArr.push(
        twoDecimal(
          netPositionData?.[asset]?.floatingAmount -
            netPositionData?.[asset]?.fixedAmount,
        ),
      );
    });

    const netPosChartData = {
      labels: Object.keys(netPositionData),
      datasets: [
        {
          data: netPosDataArr,
          backgroundColor: netPosDataArr.map((pos) => {
            return pos < 0 ? "rgb(232,77,67)" : "rgb(61,185,61)";
          }),
          hoverOffset: 4,
        },
      ],
    };

    setNetPositionChartData(netPosChartData);
  };

  const getChartData = async (
    contract_id,
    start,
    end,
    setOriginalDateRange,
  ) => {
    setLoading(true);
    try {
      let url = `${config.BASE_URL}/api/contract/chart-data`;

      const params = {};

      if (start && end) {
        params["start"] = start;
        params["end"] = end;
      }

      if (contract_id) {
        params["contractId"] = contract_id;
      }

      const response = await axios.get(url, { params });
      if (response?.data?.contracts?.length > 0) {
        prepareChartData(
          response?.data,
          granularityFilter,
          location,
          selectedAsset,
          true,
        );

        if (setOriginalDateRange) {
          setInitialDateRange({
            startDate: moment(
              response?.data?.dateFilter?.startDate,
              GRANULARITY_DATE_FORMAT.day,
            ).toDate(),
            endDate: moment(
              response?.data?.dateFilter?.endDate,
              GRANULARITY_DATE_FORMAT.day,
            ).toDate(),
          });

          setStartDate(response?.data?.dateFilter?.startDate);
          setEndDate(response?.data?.dateFilter?.endDate);
        }
        setOriginalData(response?.data);
        setMlfPrices(response?.data?.mlf);
        setCurrency(response?.data?.contracts?.[0]?.currency);
        setLocations(response?.data?.locations);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const configureGraphData = (
    input,
    filterKey,
    labelkey,
    valueKey,
    setFunction,
    dateFormat = GRANULARITY_DATE_FORMAT.day,
  ) => {
    const graphData = processGraphData(
      input,
      filterKey,
      labelkey,
      valueKey,
      dateFormat,
    );

    const { data } = graphData;

    const chartData = {
      datasets: Object.keys(data).map((filter) => {
        return {
          label: filter,
          data: data[filter],
          fill: true,
        };
      }),
    };

    setFunction(chartData);
  };

  const configureChartOptions = (xLabel, yLabel, isXDate = true) => {
    const surfaceBorder = documentStyle.getPropertyValue("--surface-border");

    const options = {
      maintainAspectRatio: true,
      aspectRatio: 0.0,
      plugins: {
        legend: {
          labels: {
            color: textColor,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
          },
          title: {
            display: true,
            text: isXDate
              ? `${xLabel} (${GRANULARITY_DATE_FORMAT[granularityFilter]})`
              : xLabel,
            color: textColorSecondary,
          },
          grid: {
            display: false,
            drawBorder: true,
          },
        },
        y: {
          ticks: {
            color: textColorSecondary,
          },
          title: {
            display: true,
            text: yLabel,
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
            drawBorder: true,
          },
        },
      },
    };

    return options;

    // setChartOptions(options);
  };

  const processGraphData = (
    input,
    filterkey,
    labelKey,
    valueKey,
    dateFormat = GRANULARITY_DATE_FORMAT.day,
  ) => {
    const data = {};

    input?.forEach((item) => {
      data[item[filterkey]] = item?.data?.map((prop) => {
        return {
          x: moment(
            prop[labelKey],
            // granularityFilter === GRANULARITY_MAPPER.Quarter
            //   ? GRANULARITY_DATE_FORMAT.month
            //   :
            dateFormat,
          ).format(
            // granularityFilter === GRANULARITY_MAPPER.Quarter
            //   ? GRANULARITY_DATE_FORMAT.month
            //   :
            dateFormat,
          ),
          y: prop[valueKey],
        };
      });
    });

    return {
      data,
    };
  };

  const handleDateChange = (value, name) => {
    if (name === "startDate") {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
  };

  const handleGranularityFilterChange = ({ target }) => {
    setGranularityFilter(target.value);
  };

  const handleFilterChange = () => {
    getChartData(contractId, startDate, endDate, false);
  };

  const handleLocationChange = ({ target }) => {
    setLocation(target.value);
  };

  const handleAssetChange = ({ target }) => {
    setSelectedAsset(target.value);
  };

  const prepareChartData = (
    originalDataArg,
    granularity,
    newLocation,
    asset,
    locationOrAssetChanged = false,
  ) => {
    let newPerformanceData = [...(originalDataArg?.performances || [])];
    // let newInvoices = [...(originalDataArg?.invoices || [])];
    let newSpotPrices = [...(originalDataArg?.spotPrices || [])];
    let newLgcs = [...(originalDataArg?.lgcs || [])];

    // Check whether the value of location or asset filter has changed
    // If yes, then filter out the contracts for the selected location and based on that filter the invoices, spot price and lgcs
    if (locationOrAssetChanged) {
      const contracts = originalDataArg?.contracts?.filter(
        (contract) =>
          (!newLocation || contract?.location === newLocation) &&
          (!asset || contract?.assetId === asset),
      );

      const newContractIds = new Set();
      const newRegionIds = new Set();

      contracts?.forEach((contract) => {
        newContractIds.add(contract?.id);
        newRegionIds.add(contract?.regionId);
      });

      // Now for these contracts, filter the performance data based on the contracts id.
      newPerformanceData =
        originalDataArg?.performances?.filter((contractPerformance) =>
          newContractIds.has(contractPerformance?.contractId),
        ) || [];

      // newInvoices =
      //   originalDataArg?.invoices?.filter((invoice) =>
      //     newContractIds.has(invoice?.id)
      //   ) || [];
      newSpotPrices =
        originalDataArg?.spotPrices?.filter((spotPrice) =>
          newRegionIds.has(spotPrice?.regionId),
        ) || [];
      newLgcs =
        originalDataArg?.lgcs?.filter(
          (lgc) => !newLocation || lgc?.location === newLocation,
        ) || [];
    }

    const granularitySpotPriceAggregate = {};
    const granularityLgcAggregate = {};
    const granularityPerformanceAggregate = {};

    // Prepare spot price data based on the selected granularity
    newSpotPrices?.forEach((region) => {
      granularitySpotPriceAggregate[region?.regionId] = {};
      region?.data?.forEach((regionSpotPrice) => {
        const date = moment(
          regionSpotPrice?.settlementDate,
          GRANULARITY_DATE_FORMAT.day,
        ).format(GRANULARITY_DATE_FORMAT[granularity]);
        if (!granularitySpotPriceAggregate[region?.regionId][date]) {
          granularitySpotPriceAggregate[region?.regionId][date] = {
            total: 0,
            count: 0,
          };
        }
        granularitySpotPriceAggregate[region?.regionId][date].total +=
          regionSpotPrice?.spotPrice;
        granularitySpotPriceAggregate[region?.regionId][date].count += 1;
      });
    });

    newSpotPrices = Object.keys(granularitySpotPriceAggregate)?.map(
      (region) => {
        const regionSpotPrices = Object.keys(
          granularitySpotPriceAggregate[region],
        )?.map((dateFormat) => {
          return {
            spotPrice:
              granularitySpotPriceAggregate[region][dateFormat]?.total /
              granularitySpotPriceAggregate[region][dateFormat]?.count,
            settlementDate: `${dateFormat}T00:00:00:000Z`,
          };
        });

        return {
          regionId: region,
          data: regionSpotPrices,
        };
      },
    );

    // Prepare LGC price data based on the selected granularity
    newLgcs?.forEach((loc) => {
      granularityLgcAggregate[loc?.location] = {};

      loc?.data?.forEach((locationLgc) => {
        const date = moment(
          locationLgc?.isoDate,
          GRANULARITY_DATE_FORMAT.day,
        ).format(GRANULARITY_DATE_FORMAT[granularity]);
        if (!granularityLgcAggregate[loc?.location][date]) {
          granularityLgcAggregate[loc?.location][date] = {
            total: 0,
            count: 0,
          };
        }
        granularityLgcAggregate[loc?.location][date].total +=
          locationLgc?.LGC || 0;
        granularityLgcAggregate[loc?.location][date].count += 1;
      });
    });

    newLgcs = Object.keys(granularityLgcAggregate)?.map((loc) => {
      const locationLGCs = Object.keys(granularityLgcAggregate[loc])?.map(
        (dateFormat) => {
          return {
            LGC:
              granularityLgcAggregate[loc][dateFormat]?.total /
              granularityLgcAggregate[loc][dateFormat]?.count,
            isoDate: `${dateFormat}T00:00:00:000Z`,
          };
        },
      );

      return {
        location: loc,
        data: locationLGCs,
      };
    });

    newPerformanceData?.forEach((contract) => {
      granularityPerformanceAggregate[contract?.contractId] = {};
      contract?.data?.forEach((contractPerformance) => {
        const date = moment(
          contractPerformance?.date,
          GRANULARITY_DATE_FORMAT.day,
        ).format(GRANULARITY_DATE_FORMAT[granularity]);
        if (!granularityPerformanceAggregate[contract?.contractId][date]) {
          granularityPerformanceAggregate[contract?.contractId][date] = {
            negativePriceCompensation: 0,
            negativePriceEvents: [],
            suppliedVolume: 0,
            fixedAmount: 0,
            floatingAmount: 0,
            contractId: contract?.contractId,
            expectedVolume: 0,
            shortFall: 0,
            surplus: 0,
            forecastedRevenue: 0,
            actualRevenue: 0,
          };
        }

        granularityPerformanceAggregate[contract?.contractId][
          date
        ].negativePriceCompensation +=
          contractPerformance?.negativePriceCompensation;
        granularityPerformanceAggregate[contract?.contractId][
          date
        ].negativePriceEvents = [
          ...granularityPerformanceAggregate[contract?.contractId][date]
            .negativePriceEvents,
          ...contractPerformance?.negativePriceEvents,
        ];
        granularityPerformanceAggregate[contract?.contractId][date].shortFall +=
          contractPerformance?.shortFall || 0;
        granularityPerformanceAggregate[contract?.contractId][date].surplus +=
          contractPerformance?.surplus || 0;
        granularityPerformanceAggregate[contract?.contractId][
          date
        ].suppliedVolume += contractPerformance?.suppliedVolume;
        granularityPerformanceAggregate[contract?.contractId][
          date
        ].fixedAmount += contractPerformance?.fixedAmount;
        granularityPerformanceAggregate[contract?.contractId][
          date
        ].floatingAmount += contractPerformance?.floatingAmount;

        granularityPerformanceAggregate[contract?.contractId][
          date
        ].expectedVolume += contractPerformance?.expectedVolume;

        granularityPerformanceAggregate[contract?.contractId][
          date
        ].forecastedRevenue += contractPerformance?.forecastedRevenue;

        granularityPerformanceAggregate[contract?.contractId][
          date
        ].actualRevenue += contractPerformance?.actualRevenue;
      });
    });

    newPerformanceData = Object.keys(granularityPerformanceAggregate)?.map(
      (contractId) => {
        const contractPerformances = Object.keys(
          granularityPerformanceAggregate[contractId],
        )?.map((dateFormat) => {
          return {
            negativePriceCompensation:
              granularityPerformanceAggregate[contractId][dateFormat]
                ?.negativePriceCompensation,
            negativePriceEvents:
              granularityPerformanceAggregate[contractId][dateFormat]
                ?.negativePriceEvents,
            shortFall:
              granularityPerformanceAggregate[contractId][dateFormat].shortFall,
            surplus:
              granularityPerformanceAggregate[contractId][dateFormat].surplus,
            suppliedVolume:
              granularityPerformanceAggregate[contractId][dateFormat]
                .suppliedVolume,
            fixedAmount:
              granularityPerformanceAggregate[contractId][dateFormat]
                .fixedAmount,
            floatingAmount:
              granularityPerformanceAggregate[contractId][dateFormat]
                .floatingAmount,
            contractId:
              granularityPerformanceAggregate[contractId][dateFormat]
                .contractId,
            expectedVolume:
              granularityPerformanceAggregate[contractId][dateFormat]
                .expectedVolume,
            forecastedRevenue:
              granularityPerformanceAggregate[contractId][dateFormat]
                .forecastedRevenue,
            actualRevenue:
              granularityPerformanceAggregate[contractId][dateFormat]
                .actualRevenue,
            date: `${dateFormat}T00:00:00:000Z`,
          };
        });

        return {
          contractId: contractId,
          data: contractPerformances,
        };
      },
    );

    // setInvoices(newInvoices);
    setContractPerformanceData(newPerformanceData);
    setSpotPrices(newSpotPrices);
    setLgcPrices(newLgcs);
  };

  const ppaNetPosition =
    kpis.cumulativeFloatingAmount - kpis.cumulativeFixedAmount;
  const lgcShortfallOrSurplus = kpis.lgcReceived - kpis.lgcExpected;
  const RE100BaselineSavings = ppaNetPosition + kpis.RE100Baseline * -1;

  const revenueDifference =
    kpis.cumulativeActualRevenue - kpis.cumulativeForecastedRevenue;

  let leftColumnJsx = [];

  const bubbleOptions = {
    responsive: true,
    plugins: {
      legend: {
        labels: {
          color: textColor,
        },
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            if (tooltipItem?.dataset?.type === "line") {
              return [tooltipItem?.raw?.y];
            } else {
              const eventDate = moment(tooltipItem.raw.y, "YYYY-MM-DD HH:mm");
              const xDate = eventDate.format("MMM D, YYYY");
              const formattedTime = eventDate.format("HH:mm");
              // const yMinutes = tooltipItem.raw.y;
              // const hours = Math.floor(yMinutes / 60);
              // const minutes = yMinutes % 60;
              // const formattedTime = moment()
              //   .hours(hours)
              //   .minutes(minutes)
              //   .format("HH:mm");
              return [`Date: ${xDate}`, `Time: ${formattedTime}`];
            }
          },
        },
      },
    },
    scales: {
      x: {
        type: "category",
        title: {
          display: true,
          text: `Date (${GRANULARITY_DATE_FORMAT[granularityFilter]})`,
          color: textColorSecondary,
        },
        ticks: {
          color: textColor,
        },
        // time: {
        //   unit: "day",
        //   tooltipFormat: "YYYY-MM-DD", // Format for tooltip
        //   displayFormats: {
        //     day: "YYYY-MM-DD", // Format for x-axis labels
        //   },
        // },
      },
      y: {
        type: "time",
        title: {
          display: true,
          text: "Event Date & Time",
          color: textColorSecondary,
        },
        time: {
          tooltipFormat: "MMM D, YYYY HH:mm", // Tooltip format
          displayFormats: {
            hour: "MMM D, YYYY HH:mm", // Display format for the y-axis
          },
        },
        ticks: {
          color: textColor,
          callback: function (value) {
            return moment(value).format("MMM D, YYYY");
            // const hours = Math.floor(value / 60);
            // const minutes = value % 60;
            // return `${String(hours).padStart(2, "0")}:${String(
            //   minutes
            // ).padStart(2, "0")}`; // Format as HH:mm
          },
        },
      },
      y1: {
        type: "linear",
        position: "right",
        title: {
          display: true,
          text: `Amount (${currency})`,
          color: textColorSecondary,
        },
        ticks: {
          color: textColor,
        },
        grid: {
          drawOnChartArea: false, // Only want the grid lines for one axis to show up
        },
      },
    },
  };

  if (view === "portfolio") {
    leftColumnJsx = [
      <div className="card mb-3" key="lgc-rec">
        <div className="graph-heading">
          <h3>LGC/REC Price Over Time</h3>
        </div>
        <Chart
          type="line"
          ref={lgcPriceChartRef}
          data={lgcPriceChartData}
          options={configureChartOptions("Date", "MWh")}
        />
      </div>,
      <div className="card mb-3" key="spot-price">
        <div className="graph-heading">
          <h3>Spot Price Over Time</h3>
        </div>
        <Chart
          type="line"
          ref={spotPriceChartRef}
          data={spotPriceChartData}
          options={configureChartOptions("Date", `${currency}/MWh`)}
        />
      </div>,
      <div className="card mb-3" key="expected-actual-lgc">
        <div className="graph-heading">
          <h3>Expected Vs Actual LGCs/RECs</h3>
        </div>
        <Chart
          type="bar"
          ref={volumeChartRef}
          data={volumeChartData}
          options={{
            plugins: {
              legend: {
                labels: {
                  color: textColor,
                },
              },
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: `Date (${GRANULARITY_DATE_FORMAT[granularityFilter]})`,
                  color: textColorSecondary,
                },
                ticks: {
                  color: textColorSecondary,
                },
              },
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: "MWh",
                  color: textColorSecondary,
                },

                ticks: {
                  color: textColorSecondary,
                },
              },
            },
          }}
        />
      </div>,
      <div className="card mb-3" key="cumulative-expected-actual-lgc">
        <div className="graph-heading">
          <h3>Cumulative Expected Vs Actual LGCs/RECs</h3>
        </div>
        <Chart
          type="line"
          ref={cumulativeVolumeChartRef}
          data={cumulativeVolumeChartData}
          options={configureChartOptions("Date", "MWh")}
        />
      </div>,
      <div className="card mb-3" key="forecasted-actual-revenue">
        <div className="graph-heading">
          <h3>
            {role === "offtaker"
              ? "Forecasted Cost Vs Actual Cost Over Time"
              : "Forecasted Revenue Vs Actual Revenue Over Time"}
          </h3>
        </div>
        <Chart
          type="line"
          ref={forecastedActualRevenueChartRef}
          data={forecastedAndActualRevenueChartData}
          options={configureChartOptions("Date", `${currency}`)}
        />
      </div>,
      <div className="card mb-3" key="net-position">
        <div className="graph-heading">
          <h3>Net Position</h3>
        </div>
        <Chart
          type="pie"
          ref={netPositionChartRef}
          data={netPositionChartData}
        />
      </div>,
    ];
  } else if (view === "summary") {
    leftColumnJsx = [
      <div className="card mb-3" key="cumulative-fixed-floating">
        <div className="graph-heading">
          <h3>Cumulative Fixed & Floating Payment & Cumulative PPA Position</h3>
        </div>
        <Chart
          type="line"
          ref={cumulativeFixedFloatingChartRef}
          data={cumulativeFixedFloatingChartData}
          options={configureChartOptions("Date", `${currency}`)}
        />
      </div>,
      <div className="card mb-3" key="expected-actual-lgc">
        <div className="graph-heading">
          <h3>Expected Vs Actual LGCs/RECs</h3>
        </div>
        <Chart
          type="bar"
          ref={volumeChartRef}
          data={volumeChartData}
          options={{
            plugins: {
              legend: {
                labels: {
                  color: textColor,
                },
              },
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: `Date (${GRANULARITY_DATE_FORMAT[granularityFilter]})`,
                  color: textColorSecondary,
                },
                ticks: {
                  color: textColorSecondary,
                },
              },
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: "MWh",
                  color: textColorSecondary,
                },
                ticks: {
                  color: textColorSecondary,
                },
              },
            },
          }}
        />
      </div>,
      <div className="card mb-3" key="supplied-volume-avg-spot-price">
        <div className="graph-heading">
          <h3>Supplied Volume & Average Spot Price</h3>
        </div>
        <Chart
          type="line"
          ref={suppliedVolumeChartRef}
          data={suppliedVolumeChartData}
          options={configureChartOptions("Date", "MWh")}
        />
      </div>,
      <div className="card mb-3" key="negative-price">
        <div className="graph-heading">
          <h3>Negative Price Compensation & Negative Price Events</h3>
        </div>
        <Chart
          type="bubble"
          ref={negativePriceChartRef}
          data={negativePriceChartData}
          options={bubbleOptions}
        />
      </div>,
      <div className="card mb-3" key="shortfall-surplus">
        <div className="graph-heading">
          <h3>Shortfall/Surplus</h3>
        </div>
        <Chart
          type="line"
          ref={shortFallSurplusChartRef}
          data={shortFallSurplusChartData}
          options={configureChartOptions("Date", "MWh")}
        />
      </div>,
      <div className="card mb-3" key="mlf">
        <div className="graph-heading">
          <h3>MLF Over Time</h3>
        </div>
        <Chart
          type="line"
          ref={mlfPriceChartRef}
          data={mlfPriceChartData}
          options={configureChartOptions("Date", "MLF (%)")}
        />
      </div>,
      <div className="card mb-3" key="forecasted-actual-revenue">
        <div className="graph-heading">
          <h3>
            {role === "offtaker"
              ? "Forecasted Cost Vs Actual Cost Over Time"
              : "Forecasted Revenue Vs Actual Revenue Over Time"}
          </h3>
        </div>
        <Chart
          type="line"
          ref={forecastedActualRevenueChartRef}
          data={forecastedAndActualRevenueChartData}
          options={configureChartOptions("Date", `${currency}`)}
        />
      </div>,
    ];
  }

  return (
    <div className="report-container">
      <div
        className={`report-filter ${
          view === "summary" ? "report-filter-summary-view" : ""
        }`}
      >
        <div className="date-range-container">
          <DatePicker
            formClass={"date"}
            CalValue={startDate}
            setCalValue={handleDateChange}
            id={"filter-start-date"}
            placeholder={"From"}
            category={"startDate"}
            helperText="From Date"
            dateFormat={GRANULARITY_DATE_FORMAT.day}
            minDate={initialDateRange.startDate}
            maxDate={initialDateRange.endDate}
            disabled={false}
          />
          <span className="date-separator"> - </span>
          <DatePicker
            formClass={"date"}
            CalValue={endDate}
            setCalValue={handleDateChange}
            id={"filter-end-date"}
            placeholder={"To"}
            category={"endDate"}
            helperText="To Date"
            dateFormat={GRANULARITY_DATE_FORMAT.day}
            minDate={
              startDate
                ? moment(startDate, GRANULARITY_DATE_FORMAT.day).toDate()
                : initialDateRange.startDate
            }
            maxDate={initialDateRange.endDate}
            disabled={false}
          />
          <button
            className="btn-primary"
            disabled={!startDate || !endDate || loading}
            onClick={handleFilterChange}
          >
            Apply
          </button>
        </div>
        <div className="local-filter">
          {view === "portfolio" && role === "generator" && (
            <div className="location-filter-container">
              <label htmlFor="report-asset">Asset</label>
              <select
                id="report-asset"
                name="reportAsset"
                value={selectedAsset}
                onChange={handleAssetChange}
              >
                <option value="">All</option>
                {originalData?.assets?.map((asset) => (
                  <option key={asset} value={asset}>
                    {asset}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="granularity-container">
            <label htmlFor="report-granularity">Date Hierarchy</label>
            <select
              id="report-granularity"
              name="granularityFilter"
              value={granularityFilter}
              onChange={handleGranularityFilterChange}
            >
              <option value="">Select Date Hierarchy</option>
              {Object.values(GRANULARITY_MAPPER).map((granularity) => {
                return (
                  <option value={granularity} key={granularity}>
                    {upperCase(granularity)}
                  </option>
                );
              })}
            </select>
          </div>
          {view === "portfolio" && (
            <div className="location-filter-container">
              <label htmlFor="report-location">Location</label>
              <select
                id="report-location"
                name="reportLocation"
                value={location}
                onChange={handleLocationChange}
              >
                <option value="">All</option>
                {locations.map((loc) => {
                  return (
                    <option value={loc} key={loc}>
                      {loc}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </div>
      </div>

      <div
        className={`report-container-wraper ${
          view === "summary" ? "report-container-wraper-summary-view" : ""
        }`}
      >
        <div className="left-column">{leftColumnJsx}</div>
        <div
          className={`right-column ${
            view === "summary" ? "right-column-summary-view" : ""
          }`}
        >
          <div className="card mb-3">
            <div className="graph-heading">
              <h3>PPA Net Position ({currency})</h3>
            </div>
            <div className="report-kpi">
              <div>Cumulative Floating PPA Position</div>
              <div
                className={
                  kpis.cumulativeFloatingAmount >= 0 ? "positive" : "negative"
                }
              >{`${numberFormat(
                kpis.cumulativeFloatingAmount,
                2,
                0,
                currency,
              )}`}</div>
            </div>
            <div className="report-kpi">
              <div>Cumulative Fixed PPA Position</div>
              <div
                className={
                  kpis.cumulativeFixedAmount >= 0 ? "positive" : "negative"
                }
              >{`${numberFormat(
                kpis.cumulativeFixedAmount,
                2,
                0,
                currency,
              )}`}</div>
            </div>
            <div className="report-kpi">
              <div>PPA Net Position</div>
              <div
                className={ppaNetPosition >= 0 ? "positive" : "negative"}
              >{`${numberFormat(ppaNetPosition, 2, 0, currency)}`}</div>
            </div>
            <div className="report-kpi">
              <div>
                {role === "offtaker" ? "Forecasted Cost" : "Forecasted Revenue"}
              </div>
              <div>{`${numberFormat(
                kpis.cumulativeForecastedRevenue,
                2,
                0,
                currency,
              )}`}</div>
            </div>
            <div className="report-kpi">
              <div>
                {role === "offtaker"
                  ? revenueDifference > 0
                    ? "Cost Surplus"
                    : "Cost Shortfall"
                  : revenueDifference > 0
                    ? "Revenue Surplus"
                    : "Revenue Shortfall"}
              </div>
              <div
                className={revenueDifference >= 0 ? "positive" : "negative"}
              >{`${numberFormat(revenueDifference, 2, 0, currency)}`}</div>
            </div>
            {role === "offtaker" && (
              <div className="report-kpi">
                <div>RE 100 Baseline</div>
                <div>{`${numberFormat(
                  kpis.RE100Baseline,
                  2,
                  0,
                  currency,
                )}`}</div>
              </div>
            )}
            {role === "offtaker" && (
              <div className="report-kpi">
                <div>Savings To RE 100 Baseline</div>
                <div
                  className={
                    RE100BaselineSavings >= 0 ? "positive" : "negative"
                  }
                >{`${numberFormat(RE100BaselineSavings, 2, 0, currency)}`}</div>
              </div>
            )}
          </div>

          <div className="card mb-3">
            <div className="graph-heading">
              <h3>LGC/REC Position</h3>
            </div>
            <div className="report-kpi">
              <div>LGCs/RECs Received</div>
              <div>{numberFormat(kpis.lgcReceived, 0)}</div>
            </div>
            <div className="report-kpi">
              <div>LGCs/RECs Expected</div>
              <div>{numberFormat(kpis.lgcExpected, 0)}</div>
            </div>
            <div className="report-kpi">
              <div>
                LGC/REC {lgcShortfallOrSurplus > 0 ? "Surplus" : "Shortfall"}
              </div>
              <div
                className={lgcShortfallOrSurplus >= 0 ? "positive" : "negative"}
              >
                {numberFormat(lgcShortfallOrSurplus, 0)}
              </div>
            </div>
          </div>

          <div className="card mb-3">
            <div className="graph-heading">
              <h3>Negative Price</h3>
            </div>
            <div className="report-kpi">
              <div>Negative Price Compensation</div>
              <div
                className={
                  kpis.negativePriceCompensation >= 0 ? "positive" : "negative"
                }
              >
                {numberFormat(kpis.negativePriceCompensation, 2, 0, currency)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
